import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, GuardResult, Router, RouterStateSnapshot } from '@angular/router';
import { AppMonitoring } from '../app-services/monitoring/monitor.app';
import { AppPwaLaunch } from '../app-services/pwa-launch/pwa-launch.app';
import { PlatformHelper } from '../helpers/platform.helper';

/** `/?home=1` forces the landing page, so an installed app can always reach it. */
export const HOME_QUERY_PARAM = 'home';

/** Logging origin — a functional guard has no instance to pass as `this`. */
const LOG_ORIGIN = 'pwaLaunchGuard';

/**
 * Redirects an installed app that cold-starts on the landing page to the campaign it was last used
 * for. The PWA manifest has one static `start_url` (`/`), so without this an installed app always
 * opens on the marketing page instead of the campaign the user installed it from.
 *
 * All the decision logic lives in `AppPwaLaunch`; this is the routing adapter. Redirecting with a
 * `UrlTree` resolves during route recognition, so `LandingPageComponent` is never constructed (no
 * marketing flash) and no page load happens — which is why this cannot interact with the reload in
 * `AppServiceWorkerUpdate`.
 */
export const pwaLaunchGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): GuardResult => {
  const appMonitoring = inject(AppMonitoring);
  const appPwaLaunch = inject(AppPwaLaunch);
  const router = inject(Router);

  // Ordered so ordinary web traffic never reaches storage.
  const path = state.url.split(/[?#]/)[0];
  if (path !== '/' && path !== '') {
    // The landing route also matches secondary-outlet-only URLs such as /(rightDrawer:campaign-type).
    return true;
  }

  if (route.queryParamMap.get(HOME_QUERY_PARAM) === '1') {
    return true;
  }

  if (router.navigated) {
    // An in-app navigation to home, not a launch.
    return true;
  }

  if (!PlatformHelper.isStandalone()) {
    return true;
  }

  try {
    const target = appPwaLaunch.resolveLaunchTarget();
    if (!target) {
      appMonitoring.logEvent(LOG_ORIGIN, 'Standalone launch had no campaign to resume');
      return true;
    }

    appMonitoring.logEvent(LOG_ORIGIN, 'Standalone launch resolved to a campaign', {
      mediaType: target.mediaType,
      campaignId: target.campaignId
    });
    return router.createUrlTree(
      appPwaLaunch.buildViewerCommands(target),
      appPwaLaunch.buildViewerNavigationExtras(target)
    );
  } catch (error: unknown) {
    // Never block the app from booting.
    appMonitoring.logException(
      LOG_ORIGIN,
      error instanceof Error ? error : 'Failed to resolve the standalone launch target'
    );
    return true;
  }
};
